import React, { FC, useContext, useEffect, useRef, useState } from 'react';
import Cookies, { CookieAttributes } from 'js-cookie';

import GlobalContext from '@/contexts/global-context';
import { LocaleContext } from '@/contexts/locale-context';
import useThemeBlock from '@/hooks/useThemeBlock';

import { GdprBanner } from './GdprBanner/render';
import { GdprModal } from './GdprModal/render';

import { GdprProps } from './typings';
import block from './block.json';

/**
 * Constants
 */
const ACCEPTED_VALUE = 'yes';
const REFUSED_VALUE = 'no';
const COOKIES_OPTIONS: CookieAttributes = {
	expires: 365,
	sameSite: 'Lax',
};
const COOKIE_NAME = 'supt-cookie-law-consent';

/**
 * COMPONENT
 */
export const Gdpr: FC<GdprProps> & BlockConfigs = ({
	configs,
	preview = false,
	theme = {},
}) => {
	const { blockTheme } = useThemeBlock(block.slug, theme);

	const [categories, setCategories] = useState([]);
	const [bannerDismissed, setBannerDismissed] = useState(true);
	const { setGdprServices } = useContext(GlobalContext);
	const { locale } = useContext(LocaleContext);

	const services = useRef({});

	const modalRef = useRef(null);

	useEffect(() => {
		const hasCookies = Cookies.get(`${COOKIE_NAME}_banner`) === 'dismiss';

		setBannerDismissed(hasCookies);

		window.addEventListener('hashchange', onHashChange);

		return () => {
			window.removeEventListener('hashchange', onHashChange);
		};
	}, []);

	// Set categories (triggered everytime the locale changes)
	useEffect(() => {
		const cats = getCategoriesSettings();
		setCategories(cats);
	}, [locale]);

	useEffect(() => {
		if (bannerDismissed) {
			Cookies.set(`${COOKIE_NAME}_banner`, 'dismiss', COOKIES_OPTIONS);
		} else {
			Cookies.remove(`${COOKIE_NAME}_banner`);
		}
	}, [bannerDismissed]);

	// ##############################
	// #region Event handler
	// ##############################

	const onPersonalizeClick = () => {
		modalRef.current.open();
	};

	const onHashChange = (event /*url*/) => {
		if (event.newURL.indexOf(`#${configs.hash}`) === -1) return;

		modalRef.current.open();

		window.history.replaceState(
			'',
			'Cookie Manager',
			window.location.pathname + window.location.search
		);
	};

	const onModalClosed = () => {
		if (window.location.hash.indexOf(`#${configs.hash}`) === -1) return;

		window.history.replaceState(
			'',
			document.title,
			window.location.pathname + window.location.search
		);
	};

	const onModalSaved = () => {
		setBannerDismissed(true);
	};

	const onCategoryChange = ({ enabled, id }) => {
		const changedCat = configs.categories.find((cat) => cat.id === id);
		setCategoryCookie(changedCat, enabled);
	};

	// ##############################
	// #endregion
	// ##############################

	const acceptAll = () => {
		setBannerDismissed(true);
		configs.categories.forEach((cat) => {
			setCategoryCookie(cat, true);
			modalRef.current.setCategoryEnabled(cat, true);
		});
	};

	const rejectAll = () => {
		setBannerDismissed(true);
		configs.categories.forEach((cat) => {
			setCategoryCookie(cat, false);
			modalRef.current.setCategoryEnabled(cat, false);
		});
	};

	const setCategoryCookie = (category, accepted = false) => {
		Cookies.set(
			`${COOKIE_NAME}_${category.id}_accepted`,
			accepted ? ACCEPTED_VALUE : REFUSED_VALUE,
			COOKIES_OPTIONS
		);

		if (category.services) {
			category.services.forEach(({ name, ...rest }) => {
				services.current = {
					...services.current,
					[name]: {
						...rest,
						isAccepted: accepted,
					},
				};
			});

			setGdprServices(services.current); // Needs to be set from a ref to avoid overriding state that wasn't updated yet
		}
	};

	// TODO :: Move to category file ??
	const getCategoriesSettings = () => {
		return configs.categories.map((cat) => {
			const cookie = Cookies.get(`${COOKIE_NAME}_${cat.id}_accepted`);
			const enabled = cookie === ACCEPTED_VALUE;

			return { ...cat, enabled };
		});
	};

	return (
		<div className="supt-gdpr">
			<GdprBanner
				bannerDismissed={bannerDismissed}
				texts={configs.texts.banner}
				onPersonalizeClick={onPersonalizeClick}
				onAcceptClick={acceptAll}
				onRejectClick={rejectAll}
				theme={{
					...blockTheme.banner,
					states: {
						hover: blockTheme.states?.hover?.banner,
					},
				}}
			/>
			<GdprModal
				ref={modalRef}
				categories={categories}
				texts={configs.texts.modal}
				onCategoryChange={onCategoryChange}
				onModalSaved={onModalSaved}
				onModalClosed={onModalClosed}
				preview={preview}
				theme={{
					...blockTheme.modal,
					states: {
						hover: blockTheme.states?.hover?.modal,
					},
				}}
			/>
		</div>
	);
};

Gdpr.slug = block.slug;
Gdpr.title = block.title;
